import React, { useState, useEffect } from "react";
import CommonModal from "../../../../components/modals/commonModel";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultBadge from "../../../../components/badge/defaultBadge";
import { HiEye } from "react-icons/hi";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import moment from "moment";
import AuditLogViewer from "./AuditLogViewer";
import { getSalaryHistory } from "../../../../../services/workspace/salaryStructure.service";
import toast from "react-hot-toast";

const SalaryHistoryModal = ({ employeeId, onClose }) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const formatDateTime = (dateString) => {
    return moment(dateString).format("DD MMM YYYY, hh:mm A");
  };

  // Fetch salary history from API
  useEffect(() => {
    const fetchHistory = async () => {
      if (!employeeId) return;

      setLoading(true);
      try {
        const response = await getSalaryHistory(employeeId, { per_page: 100 });
        const historyData = response?.data || response || [];
        
        // Transform API response to match component format
        const transformedData = historyData.map((record) => ({
          id: record.id || record.uuid,
          uuid: record.uuid,
          effectiveDate: record.effective_date || record.effectiveDate,
          structureName: record.structure_name || record.structureName || "N/A",
          createdBy: record.created_by?.name || record.createdBy || "System",
          createdAt: record.created_at || record.createdAt,
          status: record.status || "active",
          grossPay: record.gross_pay || record.grossPay || 0,
          netPay: record.net_pay || record.netPay || 0,
          ctc: record.annual_ctc || record.ctc || 0,
          components: record.components?.map((comp) => ({
            name: comp.name,
            amount: comp.amount || comp.value || 0,
            type: comp.type,
            percentage: comp.percentage,
            taxable: comp.taxable || comp.is_taxable,
            annual_amount: comp.annual_amount || 0,
          })) || [],
        }));

        setSalaryHistory(transformedData);
      } catch (error) {
        console.error("Failed to fetch salary history:", error);
        toast.error("Failed to fetch salary history");
        setSalaryHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [employeeId]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "upcoming":
        return <DefaultBadge status={1} custom="Upcoming" />;
      case "active":
        return <DefaultBadge status={1} custom="Active" />;
      case "expired":
        return <DefaultBadge status={2} custom="Expired" />;
      default:
        return <DefaultBadge status={0} custom="Inactive" />;
    }
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
  };

  const handleViewDiff = (record, previousRecord) => {
    if (!previousRecord) return;

    const diffs = [];
    record.components.forEach((comp) => {
      const prevComp = previousRecord.components.find((c) => c.name === comp.name);
      if (prevComp && prevComp.amount !== comp.amount) {
        diffs.push({
          field: comp.name,
          old: prevComp.amount.toString(),
          new: comp.amount.toString(),
        });
      }
    });

    if (record.ctc !== previousRecord.ctc) {
      diffs.push({
        field: "ctc",
        old: previousRecord.ctc.toString(),
        new: record.ctc.toString(),
      });
    }

    return diffs;
  };

  return (
    <>
      <CommonModal
        modalSize="4xl"
        headerTitle="Salary History"
        show={true}
        onClose={onClose}
      >
        <div className="flex flex-col gap-4">
          {/* Header Actions */}
          {/* <div className="flex justify-end">
            <DefaultButton
              variant="outline"
              name="View Audit Log"
              onClick={() => setShowAuditLog(true)}
            />
          </div> */}

          {/* History Table */}
          <div className="bg-white border border-borderColor rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                      Effective Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                      Structure Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                      Created By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-borderColor">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-textLight">
                        Loading salary history...
                      </td>
                    </tr>
                  ) : salaryHistory.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-textLight">
                        No salary history found
                      </td>
                    </tr>
                  ) : (
                    salaryHistory.map((record, index) => {
                      const previousRecord =
                        index < salaryHistory.length - 1
                          ? salaryHistory[index + 1]
                          : null;
                      const diffs = handleViewDiff(record, previousRecord);

                    return (
                      <tr key={record.id} className="hover:bg-primary-50">
                        <td className="px-4 py-3 text-sm text-theme">
                          {formatDate(record.effectiveDate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-theme">
                          {record.structureName}
                        </td>
                        <td className="px-4 py-3 text-sm text-theme">
                          {record.createdBy}
                        </td>
                        <td className="px-4 py-3 text-sm text-textLight">
                          {formatDateTime(record.createdAt)}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(record.status)}</td>
                        <td className="px-4 py-3">
                          <DefaultButton
                            variant="icon"
                            icon={<HiEye />}
                            size="icon"
                            onClick={() => handleViewDetails(record)}
                            tooltip="View Details"
                          />
                        </td>
                      </tr>
                    );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail View */}
          {selectedRecord && (
            <div className="bg-primary-50 rounded-lg p-4 border border-borderColor">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-theme mb-1">
                    {selectedRecord.structureName}
                  </h4>
                  <p className="text-sm text-textLight">
                    Effective from {formatDate(selectedRecord.effectiveDate)}
                  </p>
                </div>
                <DefaultButton
                  variant="secondary"
                  name="Close"
                  onClick={() => setSelectedRecord(null)}
                />
              </div>

              {/* Comparison with Previous */}
              {(() => {
                const currentIndex = salaryHistory.findIndex(
                  (r) => r.id === selectedRecord.id
                );
                const previousRecord =
                  currentIndex < salaryHistory.length - 1
                    ? salaryHistory[currentIndex + 1]
                    : null;
                const diffs = previousRecord ? handleViewDiff(selectedRecord, previousRecord) : [];

                return diffs.length > 0 ? (
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-theme mb-2">
                      Changes from Previous Structure
                    </h5>
                    <div className="grid grid-cols-2 gap-4 bg-white rounded p-3 border border-borderColor">
                      {diffs.map((diff, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-textLight w-24">{diff.field}:</span>
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs text-error-600 line-through">
                            {formatCurrency(parseFloat(diff.old))}
                          </span>
                          <FaArrowUp className="text-success-600 text-xs" />
                          <span className="text-xs font-semibold text-success-600">
                            {formatCurrency(parseFloat(diff.new))}
                          </span>
                        </div>
                      </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Components */}
              <div>
                <h5 className="text-sm font-semibold text-theme mb-2">Components</h5>
                <div className="bg-white rounded p-3 border border-borderColor">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borderColor">
                        <th className="text-left py-2 text-theme font-medium">Component</th>
                        <th className="text-right py-2 text-theme font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRecord.components
                        .filter(comp => comp.type !== "deduction")
                        .map((comp, idx) => (
                          <tr key={idx} className="border-b border-borderColor">
                            <td className="py-2 text-theme">{comp.name}</td>
                            <td className="text-right py-2 font-medium text-theme">
                              {formatCurrency(comp.amount)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-theme">
                        <td className="py-2 font-semibold text-theme">Gross Pay</td>
                        <td className="text-right py-2 font-semibold text-theme">
                          {formatCurrency(selectedRecord.grossPay)}
                        </td>
                      </tr>
                      {selectedRecord.components.filter(comp => comp.type === "deduction").length > 0 && (
                        <>
                          {selectedRecord.components
                            .filter(comp => comp.type === "deduction")
                            .map((comp, idx) => (
                              <tr key={`deduction-${idx}`}>
                                <td className="py-2 text-error-600 "> {comp.name}</td>
                                <td className="text-right py-2 text-error-600">
                                  -{formatCurrency(comp.amount)}
                                </td>
                              </tr>
                            ))}
                        </>
                      )}
                      <tr>
                        <td className="py-2 font-semibold text-theme">Total Deductions</td>
                        <td className="text-right py-2 font-semibold text-error-600">
                          -{formatCurrency(selectedRecord.grossPay - selectedRecord.netPay)}
                        </td>
                      </tr>
                      <tr className="border-t border-borderColor">
                        <td className="py-2 font-semibold text-success-600">Net Pay</td>
                        <td className="text-right py-2 font-semibold text-success-600">
                          {formatCurrency(selectedRecord.netPay)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 font-semibold text-theme">Annual CTC</td>
                        <td className="text-right py-2 font-semibold text-theme">
                          {formatCurrency(selectedRecord.ctc)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </CommonModal>

      {showAuditLog && (
        <AuditLogViewer
          employeeId={employeeId}
          onClose={() => setShowAuditLog(false)}
        />
      )}
    </>
  );
};

export default SalaryHistoryModal;

